import React, { useEffect, useRef, useState } from 'react';
const backNews = '/icons/backNews.svg';
import styles from './SingleNewsPage.module.scss';
import Text from '../../../atoms/text/Text';
const calendar = '/icons/calendar.svg';
const clock = '/icons/clock.svg';
const facebook = '/icons/socials/facebook2.svg';
const twitter = '/icons/socials/twitter2.svg';
const whatsapp = '/icons/socials/whatsapp_2_.svg';
const CopyLink = '/icons/copyLink.svg';
const location = '/icons/location-pin.svg';

import defaultImage from '../../../../assets/images/defaultNews.png';
import { useNavigate, useParams } from 'react-router-dom';
import HtmlTag from '../../../molecule/htmlTag/HtmlTag';
import Carousel from '../../../molecule/carousel/Carousel';
import useScrollToTop from '../../../../app/hooks/useScrollToTop';
import { formatDate, formatTime } from '../../../molecule/eventCard/EventCard';
import useIsRtl from '../../../../app/helpers/isRtl';
import { useGetQuery } from '../../../../services/apiCall';
import endpoints from '../../../../api/endpoints';
import CountdownTimer from './CountdownTimer';
import Loader from '../../../atoms/loader/Loader';
import { successMessage } from '../../../atoms/toast/Toast';
import InnerButton from '../../../atoms/innerButton/innerButton';
import parseEventStart from '../../../../app/helpers/parseEventStart';
const clockSvg = '/assets/svg/cards/clock.svg';

const inFiveDays = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000);
const iso = inFiveDays.toISOString();

const Slide = ({ image }) => {
  const handleError = (e) => {
    e.target.src = defaultImage;
  };

  return (
    <div className={styles['image']}>
      <img className={styles['img']} src={image} alt="" onError={handleError} />
    </div>
  );
};

const getNormalizedContent = ({ data, type }) => {
  if (!data) return {};
  switch (type) {
    case 'news':
      return {
        title: data.title_AR,
        date: data.newsDate,
        description: data.newsTextAr,
        image: `${data.url}${data.image}`,
        images: data.image ? [`${data.url}${data.image}`] : [],
      };
    case 'events':
      return {
        title: data?.title_AR || '',
        date: data?.eventDate,
        time: data?.eventDate,
        eventDate: data?.eventDate,
        locationLink: data?.locationLink || '',
        description: data?.eventText_EN || data?.eventText_AR || '',
        images: [
          data?.image1 ? `${data.url}${data.image1}` : defaultImage,
          data?.image2 ? `${data.url}${data.image2}` : defaultImage,
          data?.image3 ? `${data.url}${data.image3}` : defaultImage,
        ],
        registerLink: data?.registerLink || '',
      };
    case 'media':
      return {
        title: data.galleryName_AR,
        images: data.images?.map((img) => `${img.url}${img.imageURL}`),
      };
    case 'newsletter':
      return {
        title: data.title_AR,
        date: data.newsletterDate,
        description: data.body_AR,
        image: `${data.url}${data.newsletterImage}`,
        images: data.newsletterImage
          ? [`${data.url}${data.newsletterImage}`]
          : [],
      };
    default:
      return {};
  }
};

const SingleNewsPage = () => {
  const thumbnailsRef = useRef();
  useScrollToTop();
  const navigate = useNavigate();
  const { id: rawId } = useParams();
  const dashIndex = rawId.indexOf('-');
  let type = rawId.substring(0, dashIndex);
  const actualId = rawId.substring(dashIndex + 1);
  const isRtl = useIsRtl();

  if (!type || type.trim() === '') {
    type = 'news';
  }

  const [currentSlide, setCurrentSlide] = useState(0);
  let apiUrl = '';
  if (type === 'news') {
    apiUrl = `${endpoints.news.getNewsById}${actualId}`;
  } else if (type === 'media') {
    apiUrl = `${endpoints.news.mediaGalleryById}${actualId}`;
  } else if (type === 'events') {
    apiUrl = `${endpoints.news.getEventsById}${actualId}`;
  } else {
    apiUrl = `${endpoints.news.getNewsletterById}${actualId}`;
  }

  const { data, error, isLoading, refetch } = useGetQuery({ apiUrl });

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (thumbnailsRef.current) {
      const container = thumbnailsRef.current;
      const activeThumb = container.children[currentSlide];

      if (activeThumb) {
        const containerWidth = container.offsetWidth;
        const thumbOffsetLeft = activeThumb.offsetLeft;
        const thumbWidth = activeThumb.offsetWidth;

        const scrollLeft =
          thumbOffsetLeft - containerWidth / 2 + thumbWidth / 2;

        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth',
        });
      }
    }
  }, [currentSlide]);

  const content = getNormalizedContent({ data, type });

  const canShowRegister = (dateStr, timeStr) => {
    const start = parseEventStart(dateStr, timeStr);
    if (!start) return false;
    return Date.now() < start.getTime();
  };

  return (
    <div className={styles['single-news-page']}>
      {isLoading && <Loader fullPage />}

      <div
        className={`${styles['page-header']} ${isRtl ? styles['header-rtl'] : ''}`}
      >
        <span className={styles['span']}>
          <img src={backNews} alt="" onClick={() => navigate(-1)} />
        </span>
        <Text
          title={`common.${type}`}
          variant="text-bold-25-black"
          color="#1a1a1a"
          className={styles.backTitle}
          isLineBreak
          style={{ direction: isRtl ? 'rtl' : '' }}
        />
      </div>

      <div className={styles['news-content']}>
        <div className={styles['news-description']}>
          <div
            className={`${styles['title-container']} ${isRtl ? 'rtl' : ''} ${type === 'media' || type === 'newsletter' ? styles['media-ui'] : ''}`}
          >
            <div className={styles.left}>
              <div className={styles['carousel-container']}>
                <div
                  className={`${styles['image-container']} ${
                    type === 'newsletter' ? styles['newsletter-image'] : ''
                  }`}
                >
                  {Array.isArray(content?.images) &&
                  content?.images.length > 0 &&
                  type !== 'newsletter' ? (
                    <Carousel
                      components={content?.images.map((img, index) => (
                        <Slide key={index} image={img} />
                      ))}
                      style={{ height: '100%' }}
                      hasArrows={content?.images.length > 1}
                      hasDots={false}
                      nextStyle={styles.nextArrow}
                      prevStyle={styles.prevArrow}
                      className={styles['carousel']}
                      currentSlide={currentSlide}
                      getSlide={setCurrentSlide}
                      slideClassName={styles['slide-class']}
                    />
                  ) : (
                    <Slide
                      image={content?.image}
                      className={styles['carousel-image']}
                    />
                  )}
                </div>

                <div className={styles.carouselImages} ref={thumbnailsRef}>
                  {Array.isArray(content?.images) &&
                    content?.images.length > 1 &&
                    content?.images.map((img, index) => (
                      <img
                        key={index}
                        src={img}
                        alt={`thumb-${index}`}
                        className={`${styles.thumbnail} ${
                          index === currentSlide ? styles.activeThumbnail : ''
                        }`}
                        onClick={() => setCurrentSlide(index)}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = defaultImage;
                        }}
                      />
                    ))}
                </div>
              </div>
            </div>

            <div className={styles.right}>
              <div className={styles['info-cont']}>
                {type === 'events' ? (
                  <div
                    className={`${styles['tag-head']} ${isRtl ? styles['rtl-tag'] : ''}`}
                  >
                    <img src={clockSvg} alt="" />
                    <Text
                      title={
                        new Date(content?.date) < new Date()
                          ? 'common.date_expired'
                          : formatDate(content?.date)
                      }
                      variant="text-regular-12"
                      className={`${styles.eventDate}`}
                      style={{ direction: 'ltr' }}
                    />
                  </div>
                ) : null}
                {type === 'news' && (
                  <Text
                    title={formatDate(content?.date)}
                    variant="text-regular-16"
                    color="#1a1a1a"
                    className={`${styles.date} notranslate`}
                    style={{ direction: isRtl ? 'ltr' : '' }}
                  />
                )}
                <Text
                  title={content?.subtitle ?? content?.title}
                  variant="text-bold-italic-33"
                  color="#1a1a1a"
                  className={styles.title}
                />
                {type === 'events' && !isLoading && (
                  <CountdownTimer
                    targetDate={content?.date}
                    className={styles.countdown}
                    isRtl={isRtl}
                  />
                )}
                {type === 'events' && !isLoading && (
                  <div className={styles['event-details']}>
                    <div
                      className={`${styles.dates} ${isRtl ? styles['rtl-dates'] : ''}`}
                    >
                      <Text
                        svg={calendar}
                        svgPosition="left"
                        title={formatDate(content?.date)}
                        variant="text-regular-16"
                        color="#1a1a1a"
                        className={`${styles.text} notranslate`}
                        fill="brightness(0) saturate(100%)"
                      />
                      <span className={styles['line']}>/</span>
                      <Text
                        svgPosition={isRtl ? 'right' : 'left'}
                        svg={clock}
                        title={formatTime(content?.time)}
                        variant="text-regular-16"
                        color="#1a1a1a"
                        className={
                          !isRtl
                            ? styles.text
                            : `${styles.text} ${styles.rtlText}`
                        }
                        fill="brightness(0) saturate(100%)"
                        style={{ direction: 'ltr' }}
                      />
                    </div>
                    <Text
                      svg={location}
                      svgPosition="left"
                      title={content?.locationLink}
                      variant="text-regular-16"
                      className={`${styles.locationLink} ${isRtl ? styles['locationLink-rtl'] : ''}`}
                      fill="brightness(0) saturate(100%)"
                      color="#1a1a1a"
                    />
                  </div>
                )}
                {type === 'events' &&
                  !isLoading &&
                  (content?.registerLink &&
                  canShowRegister(content?.date, content?.time) ? (
                    <a
                      href={
                        content.registerLink.startsWith('http')
                          ? content.registerLink
                          : `https://${content.registerLink}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${styles.registerLink} ${isRtl ? styles['registerLink-rtl'] : ''}`}
                    >
                      <InnerButton title="home.register" />
                    </a>
                  ) : null)}
              </div>
            </div>
          </div>
        </div>

        <div className={styles['content-body']}>
          <div className={styles['desc-container']}>
            <HtmlTag
              title={content?.description}
              variant="text-regular-20"
              color="#1a1a1a"
              className={styles.desc}
              externalLineHeight={1.4}
              containerStyle={{ direction: isRtl ? 'rtl' : '' }}
            />
          </div>
        </div>
      </div>

      <div
        className={`${styles['footer']} ${isRtl ? styles['footer-rtl'] : ''}`}
      >
        <Text
          title="common.didYouLikeIt"
          variant="text-bold-italic-22"
          color="#1a1a1a"
          className={styles.shareText}
        />
        <div className={styles['social-media']}>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
              window.location.href
            )}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={facebook}
              alt="Share on Facebook"
              loading="lazy"
              decoding="async"
            />
          </a>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
              `${content?.title}\n\n${window.location.href}`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={twitter}
              alt="Share on Twitter"
              loading="lazy"
              decoding="async"
            />
          </a>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(
              `${content?.title}\n\n${window.location.href}`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={whatsapp}
              alt="Share on WhatsApp"
              loading="lazy"
              decoding="async"
            />
          </a>
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              successMessage('common.copied_successfully');
            }}
            className={styles.copyButton}
          >
            <img
              src={CopyLink}
              alt="Copy Link"
              loading="lazy"
              decoding="async"
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SingleNewsPage;
